import { trackArtifactEdit } from "@/components/artifacts/view/pendingArtifactEdits.ts";
import { eventQueries } from "@/components/calendars/api.ts";
import {
  parseFlexibleMeetingIds,
  stringifyFlexibleMeetingIds,
} from "@/components/preferences/api.ts";
import {
  useSurfacePreferences,
  useUpdateSurfacePreferences,
} from "@/components/preferences/usePreferences.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import type { RecurringSeries } from "@/types/events.ts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

/**
 * The user's recurring meetings, and which of them the scheduler is allowed to
 * move — with the one way to change that.
 *
 * Shared rather than reimplemented per surface. The same question is asked in
 * scheduling settings, on the canvas picker card, and in the omnibox picker
 * card, and the answer is stored inverted: the preference lists the *movable*
 * meetings, and everything absent from it is protected. That polarity is the
 * thing most likely to be got backwards by a second copy, and getting it
 * backwards means the scheduler booking over a meeting the user protected.
 *
 * Saving is per-toggle rather than behind a Save button, so a user who abandons
 * a card halfway keeps every meeting they had already marked.
 *
 * Every write registers with {@link trackArtifactEdit}. Both picker surfaces sit
 * next to a Done that POSTs and has the server read this preference straight
 * back to say what the user chose; without registering, a toggle and the Done
 * that follows it race, and the shorter server path wins — so the message names
 * the set as it was one click ago (the shape of BIG-6261).
 */
export function useFlexibleMeetings() {
  const [isSaving, setIsSaving] = useState(false);
  const { data, isLoading } = useQuery(eventQueries.frequentRecurringSeries());
  const { flexibleMeetingIds } = useSurfacePreferences("scheduling");
  const updateSchedulingPreferences = useUpdateSurfacePreferences("scheduling");

  const series = useMemo<RecurringSeries[]>(() => data?.series ?? [], [data?.series]);

  const flexibleIds = useMemo(
    () => parseFlexibleMeetingIds(flexibleMeetingIds),
    [flexibleMeetingIds],
  );
  const flexibleIdSet = useMemo(() => new Set(flexibleIds), [flexibleIds]);

  const canMove = useCallback(
    (externalRecurringEventId: string) => flexibleIdSet.has(externalRecurringEventId),
    [flexibleIdSet],
  );

  /**
   * Flip one series and save. The preferences mutation updates its cache
   * optimistically, so the row repaints on the next render rather than waiting
   * on the round trip.
   *
   * The saved list is written back whole, including ids for series that are no
   * longer in `series` — a meeting that has dropped out of the query's window
   * (a fortnight of lookahead) is not one the user un-marked, and dropping it
   * here would silently re-protect it.
   */
  const toggle = useCallback(
    async (externalRecurringEventId: string) => {
      setIsSaving(true);
      try {
        const nextIds = flexibleIdSet.has(externalRecurringEventId)
          ? flexibleIds.filter((id) => id !== externalRecurringEventId)
          : [...flexibleIds, externalRecurringEventId];
        await trackArtifactEdit(
          updateSchedulingPreferences({
            flexibleMeetingIds: stringifyFlexibleMeetingIds(nextIds),
          }),
        );
        // No success toast: the row flips in place, and a toast per toggle turns
        // a quick pass over the list into a notification stream.
      } catch {
        errorToast("Failed to update protected meetings. Please try again.");
      } finally {
        setIsSaving(false);
      }
    },
    [flexibleIds, flexibleIdSet, updateSchedulingPreferences],
  );

  return {
    series,
    isLoading,
    isSaving,
    canMove,
    toggle,
    /** How many of the listed series the scheduler still may not touch. */
    protectedCount: series.filter((s) => !flexibleIdSet.has(s.externalRecurringEventId)).length,
  };
}
